"use client";

import { lazy, Suspense, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import Link from "next/link";

const WebsiteAct2WebGL = lazy(() => import("./WebsiteAct2WebGL"));
const MarketingAct2WebGL = lazy(() => import("./MarketingAct2WebGL"));
const AIAgentAct2WebGL = lazy(() => import("./AIAgentAct2WebGL"));
const SaaSSynchronizedAct2 = lazy(() => import("./SaaSAct2Premium"));

type Pathway = "website" | "marketing" | "ai" | "saas";
type FilmMode = "tour" | "deep";
type FilmStage = "question" | "question-exit" | "countdown" | "website-intro" | "website" | "website-exit" | "marketing-intro" | "marketing" | "marketing-exit" | "ai-intro" | "ai" | "ai-exit" | "saas-intro" | "saas" | "saas-exit" | "settled";

const QUESTION_EXIT_MS = 400;
const OPENING_HOLD_MS = 3500;
const COUNTDOWN_STEP_MS = 1000;
const TOUR_CHAPTER_MS = 7600;
const TOUR_CROSSFADE_MS = 700;
const ACT_CROSSFADE_MS = 800;

const PATHWAYS: Array<{ id: Pathway; label: string; short: string; description: string }> = [
  { id: "website", label: "a website", short: "Website", description: "Digital experiences built to hold attention" },
  { id: "marketing", label: "a marketing engine", short: "Marketing", description: "Search, social and email moving as one" },
  { id: "ai", label: "an AI agent", short: "AI Agents", description: "Requests understood, completed and verified" },
  { id: "saas", label: "a SaaS product", short: "SaaS", description: "Modern tools synchronized into one product" },
];

const NEXT_PATHWAY: Record<Pathway, Pathway | null> = { website: "marketing", marketing: "ai", ai: "saas", saas: null };

const STUDIO_TOOLS = [
  ["ChatGPT", "chatgpt"], ["Claude", "claude"], ["Vercel", "vercel"], ["GitHub", "github"],
  ["LinkedIn", "linkedin"], ["Meta", "meta"], ["Google Ads", "google"], ["ClickUp", "clickup"],
  ["Supabase", "supabase"], ["Stripe", "stripe"], ["Replicate", "replicate"], ["Resend", "resend"],
] as const;

const OPENAI_PATH = "M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z";
const LINKEDIN_PATH = "M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z";

const TOOL_LOGO_PATHS: Record<(typeof STUDIO_TOOLS)[number][1], string> = {
  chatgpt: OPENAI_PATH,
  claude: "m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z",
  vercel: "m12 1.608 12 20.784H0Z",
  github: "M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12",
  linkedin: LINKEDIN_PATH,
  meta: "M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z",
  google: "M3.9998 22.9291C1.7908 22.9291 0 21.1383 0 18.9293s1.7908-3.9998 3.9998-3.9998 3.9998 1.7908 3.9998 3.9998-1.7908 3.9998-3.9998 3.9998zm19.4643-6.0004L15.4632 3.072C14.3586 1.1587 11.9121.5028 9.9988 1.6074S7.4295 5.1585 8.5341 7.0718l8.0009 13.8567c1.1046 1.9133 3.5511 2.5679 5.4644 1.4646 1.9134-1.1046 2.568-3.5511 1.4647-5.4644zM7.5137 4.8438L1.5645 15.1484A4.5 4.5 0 0 1 4 14.4297c2.5597-.0075 4.6248 2.1585 4.4941 4.7148l3.2168-5.5723-3.6094-6.25c-.4499-.7793-.6322-1.6394-.5878-2.4784z",
  clickup: "M2 18.439l3.69-2.828c1.961 2.56 4.044 3.739 6.363 3.739 2.307 0 4.33-1.166 6.203-3.704L22 18.405C19.298 22.065 15.941 24 12.053 24 8.178 24 4.788 22.078 2 18.439zM12.04 6.15l-6.568 5.66-3.036-3.52L12.055 0l9.543 8.296-3.05 3.509z",
  supabase: "M11.9 1.036c-.015-.986-1.26-1.41-1.874-.637L.764 12.05C-.33 13.427.65 15.455 2.409 15.455h9.579l.113 7.51c.014.985 1.259 1.408 1.873.636l9.262-11.653c1.093-1.375.113-3.403-1.645-3.403h-9.642z",
  stripe: "M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z",
  replicate: "M24 10.262v2.712h-9.518V24h-3.034V10.262zm0-5.131v2.717H8.755V24H5.722V5.131zM24 0v2.717H3.034V24H0V0z",
  resend: "M14.679 0c4.648 0 7.413 2.765 7.413 6.434s-2.765 6.434-7.413 6.434H12.33L24 24h-8.245l-8.88-8.44c-.636-.588-.93-1.273-.93-1.86 0-.831.587-1.565 1.713-1.883l4.574-1.224c1.737-.465 2.936-1.81 2.936-3.572 0-2.153-1.761-3.4-3.939-3.4H0V0z",
};

function ToolLogo({ brand }: { brand: (typeof STUDIO_TOOLS)[number][1] }) {
  return <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><path d={TOOL_LOGO_PATHS[brand]} /></svg>;
}

function StudioToolOrbit() {
  return <div className="studio-tool-orbit" aria-hidden="true">
    {STUDIO_TOOLS.map(([name, brand], index) => <span key={name} className={`studio-tool studio-tool--${brand}`} style={{ "--tool-index": index } as React.CSSProperties}><i><ToolLogo brand={brand} /></i><b>{name}</b></span>)}
  </div>;
}

function writeStorage(storage: "local" | "session", key: string, value: string) {
  try { (storage === "local" ? window.localStorage : window.sessionStorage).setItem(key, value); }
  catch { /* File previews and privacy modes may disable browser storage. */ }
}

function removeStorage(storage: "local" | "session", key: string) {
  try { (storage === "local" ? window.localStorage : window.sessionStorage).removeItem(key); }
  catch { /* Keep the film functional when storage is unavailable. */ }
}

function pathwayForStage(stage: FilmStage): Pathway | null {
  if (stage.startsWith("website")) return "website";
  if (stage.startsWith("marketing")) return "marketing";
  if (stage.startsWith("ai")) return "ai";
  if (stage.startsWith("saas")) return "saas";
  return null;
}

function isChapterIntro(stage: FilmStage) {
  return stage.endsWith("-intro");
}

function MagneticChip({ id, label, description, selected, onSelect, onEngage, onHoverChange }: {
  id: Pathway; label: string; description: string; selected: boolean;
  onSelect: (pathway: Pathway) => void; onEngage: () => void; onHoverChange: (hovered: boolean) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "touch" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const button = buttonRef.current;
    if (!button) return;
    const bounds = button.getBoundingClientRect();
    const x = event.clientX - (bounds.left + bounds.width / 2);
    const y = event.clientY - (bounds.top + bounds.height / 2);
    if (Math.hypot(x, y) > Math.max(bounds.width, bounds.height) / 2 + 40) return;
    button.style.setProperty("--magnetic-x", `${x * 0.12}px`);
    button.style.setProperty("--magnetic-y", `${y * 0.16}px`);
  };
  const resetMagnet = () => {
    buttonRef.current?.style.setProperty("--magnetic-x", "0px");
    buttonRef.current?.style.setProperty("--magnetic-y", "0px");
  };
  return (
    <button ref={buttonRef} className="film-pathway" data-selected={selected ? "true" : "false"} type="button" aria-pressed={selected}
      onClick={() => onSelect(id)} onPointerEnter={() => onHoverChange(true)} onFocus={onEngage} onPointerMove={onPointerMove}
      onPointerLeave={() => { onHoverChange(false); resetMagnet(); }} onBlur={resetMagnet}>
      <span>{label}</span><small>{description}</small>
    </button>
  );
}

function FilmSettle({ onReplay, arriving = false }: { onReplay: () => void; arriving?: boolean }) {
  const scrollToWork = () => {
    const work = document.getElementById("work-top");
    if (work) work.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.location.assign("/#work-top");
  };
  const scrollToContact = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <section className={`film-settle film-settle--checkpoint${arriving ? " film-settle--arriving" : ""}`} aria-label="Film conclusion">
      <div className="film-settle-content">
        <p className="film-settle-eyebrow">One operator · Four disciplines · Built end to end</p>
        <h1>Not a studio. <em>A workshop.</em></h1>
        <p className="film-settle-subtitle">Websites, campaigns, agents and products — built end to end.</p>
        <div className="film-settle-actions">
          <button className="film-work-cta" type="button" onClick={scrollToWork}><span aria-hidden="true" />See the work ↓</button>
          <button className="film-contact-cta" type="button" onClick={scrollToContact}>Start a conversation</button>
        </div>
        <button className="film-replay" type="button" onClick={onReplay}>↻ Watch the studio film again</button>
      </div>
      <button className="film-work-nudge" type="button" onClick={scrollToWork} aria-label="Scroll to see the work"><span>Scroll to see the work</span><i aria-hidden="true">↓</i></button>
      <button className="film-sift-peek" type="button" onClick={scrollToWork} aria-label="Continue to SIFT selected work"><span>Next · Selected work</span><strong>SIFT</strong><i>Precision over volume ↓</i></button>
    </section>
  );
}

function FilmDirector({ stage, mode, paused, onPause, onNext, onChapter }: {
  stage: FilmStage; mode: FilmMode; paused: boolean; onPause: () => void; onNext: () => void; onChapter: (pathway: Pathway) => void;
}) {
  const active = pathwayForStage(stage);
  if (!active) return null;
  const activeIndex = PATHWAYS.findIndex((pathway) => pathway.id === active);
  const next = NEXT_PATHWAY[active];
  return (
    <div className="film-director" data-mode={mode} data-paused={paused ? "true" : "false"}>
      <div className="film-director-status"><span>{mode === "tour" ? "Averr Studios film" : "Capability deep dive"}</span><button type="button" onClick={onPause} aria-label={paused ? "Resume film" : "Pause film"}>{paused ? "Play" : "Pause"}</button></div>
      <nav className="film-chapters" aria-label="Film chapters">
        {PATHWAYS.map((pathway, index) => (
          <button key={pathway.id} type="button" data-active={pathway.id === active ? "true" : "false"} data-complete={index < activeIndex ? "true" : "false"} onClick={() => onChapter(pathway.id)}>
            <i>{String(index + 1).padStart(2, "0")}</i><span>{pathway.short}</span><b aria-hidden="true" />
          </button>
        ))}
      </nav>
      <button className="film-next-chapter" type="button" onClick={onNext}>
        <span>{next ? "Next chapter" : "Complete the film"}</span><strong>{next ? PATHWAYS.find((pathway) => pathway.id === next)?.short : "See the work"} <i aria-hidden="true">→</i></strong>
        {mode === "tour" && !paused && <b key={stage} aria-hidden="true" />}
      </button>
    </div>
  );
}

function FilmCountdown({ count, pathway }: { count: number; pathway?: Pathway }) {
  const label = pathway ? PATHWAYS.find((item) => item.id === pathway)?.short : "Film";
  return (
    <section className="film-countdown" data-pathway={pathway ?? "film"} aria-live="polite" aria-label={`${label} begins in ${count}`}>
      <p>{label} begins in</p>
      <strong key={count}>{count}</strong>
      <span>{pathway ? "Next chapter" : "Website · Marketing · AI Agents · SaaS"}</span>
    </section>
  );
}

function MatchCut({ stage }: { stage: FilmStage }) {
  const from = pathwayForStage(stage);
  if (!stage.endsWith("-exit") || !from) return null;
  return <div className="film-match-cut" data-from={from} aria-hidden="true"><i /><i /><i /><span /></div>;
}

export default function Lab() {
  const [selected, setSelected] = useState<Pathway | null>(null);
  const [stage, setStage] = useState<FilmStage>("question");
  const [mode, setMode] = useState<FilmMode | null>(null);
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const [autoCue, setAutoCue] = useState(false);
  const [autoCountdown, setAutoCountdown] = useState(3);
  const [autoCancelled, setAutoCancelled] = useState(false);
  const [hoveringPathway, setHoveringPathway] = useState(false);
  const leftLandingAfterFilm = useRef(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      setStage("question");
      setAutoCancelled(false);
      setReady(true);
      if (!window.location.hash) {
        window.history.scrollRestoration = "manual";
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (stage !== "question" || autoCancelled || hoveringPathway || mode || !ready) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const cue = window.setTimeout(() => setAutoCue(true), 900);
    const start = window.setTimeout(() => {
      setSelected("website"); setMode("tour"); setAutoCue(false); setAutoCountdown(3); setStage("countdown");
    }, OPENING_HOLD_MS);
    return () => [cue, start].forEach(window.clearTimeout);
  }, [autoCancelled, hoveringPathway, mode, ready, stage]);

  useEffect(() => {
    let timeout: number | undefined;
    if (stage === "question-exit") {
      timeout = window.setTimeout(() => setStage(`${selected ?? "website"}-intro` as FilmStage), QUESTION_EXIT_MS);
    } else if (stage === "countdown" && !paused) {
      timeout = window.setTimeout(() => {
        if (autoCountdown > 1) setAutoCountdown((value) => value - 1);
        else setStage("website-intro");
      }, COUNTDOWN_STEP_MS);
    } else if (isChapterIntro(stage) && !paused) {
      const current = pathwayForStage(stage);
      timeout = window.setTimeout(() => {
        if (autoCountdown > 1) setAutoCountdown((value) => value - 1);
        else setStage(current ?? "website");
      }, COUNTDOWN_STEP_MS);
    } else if ((stage === "website" || stage === "marketing" || stage === "ai" || stage === "saas") && mode === "tour" && !paused) {
      window.scrollTo({ top: 0, behavior: "auto" });
      timeout = window.setTimeout(() => setStage(`${stage}-exit` as FilmStage), TOUR_CHAPTER_MS);
    } else if (stage.endsWith("-exit")) {
      const current = pathwayForStage(stage);
      timeout = window.setTimeout(() => {
        if (current && NEXT_PATHWAY[current]) {
          const next = NEXT_PATHWAY[current] as Pathway;
          setSelected(next); setAutoCountdown(3); setStage(`${next}-intro` as FilmStage);
        } else {
          writeStorage("session", "averr-film-seen", "true"); setStage("settled");
        }
      }, mode === "tour" ? TOUR_CROSSFADE_MS : ACT_CROSSFADE_MS);
    }
    return () => { if (timeout !== undefined) window.clearTimeout(timeout); };
  }, [autoCountdown, mode, paused, selected, stage]);

  useEffect(() => {
    const onVisibility = () => { if (mode === "tour") setPaused(document.hidden); };
    const onKeyDown = (event: KeyboardEvent) => {
      const active = pathwayForStage(stage);
      if (!active || isChapterIntro(stage)) return;
      if (event.key === "ArrowRight") { event.preventDefault(); setStage(`${active}-exit` as FilmStage); }
      else if (event.key === " ") { event.preventDefault(); setPaused((value) => !value); }
    };
    document.addEventListener("visibilitychange", onVisibility); window.addEventListener("keydown", onKeyDown);
    return () => { document.removeEventListener("visibilitychange", onVisibility); window.removeEventListener("keydown", onKeyDown); };
  }, [mode, stage]);

  useEffect(() => {
    if (!ready || stage === "settled") return;
    const cancelFilmOnScroll = () => {
      if (window.scrollY < Math.min(120, window.innerHeight * 0.12)) return;
      writeStorage("session", "averr-film-seen", "true");
      setAutoCancelled(true);
      setAutoCue(false);
      setMode(null);
      setPaused(false);
      setSelected(null);
      setStage("settled");
    };
    window.addEventListener("scroll", cancelFilmOnScroll, { passive: true });
    return () => window.removeEventListener("scroll", cancelFilmOnScroll);
  }, [ready, stage]);

  useEffect(() => {
    if (!ready || stage !== "settled") {
      leftLandingAfterFilm.current = false;
      return;
    }
    const restoreLandingOnReturn = () => {
      const y = window.scrollY;
      if (y > window.innerHeight * .45) leftLandingAfterFilm.current = true;
      if (!leftLandingAfterFilm.current || y > 80) return;
      leftLandingAfterFilm.current = false;
      setSelected(null);
      setMode(null);
      setPaused(false);
      setAutoCue(false);
      setAutoCountdown(3);
      setAutoCancelled(true);
      setStage("question");
    };
    window.addEventListener("scroll", restoreLandingOnReturn, { passive: true });
    restoreLandingOnReturn();
    return () => window.removeEventListener("scroll", restoreLandingOnReturn);
  }, [ready, stage]);

  const cancelAutoStart = () => { setAutoCancelled(true); setAutoCue(false); };
  const setPathwayHover = (hovered: boolean) => {
    setHoveringPathway(hovered);
    if (hovered) { setAutoCue(false); setAutoCountdown(3); }
  };
  const selectPathway = (pathway: Pathway) => {
    cancelAutoStart(); setSelected(pathway); setMode("deep"); setPaused(false); setAutoCountdown(3);
    writeStorage("local", "averr-film-pathway", pathway);
    if (stage === "question") setStage("question-exit");
  };
  const startTour = () => { setSelected("website"); setMode("tour"); setPaused(false); setAutoCue(false); setAutoCountdown(3); setStage("countdown"); };
  const nextChapter = () => { const current = pathwayForStage(stage); if (current) setStage(`${current}-exit` as FilmStage); };
  const jumpToChapter = (pathway: Pathway) => { setMode("deep"); setPaused(false); setSelected(pathway); setAutoCountdown(3); setStage(`${pathway}-intro` as FilmStage); };
  const skipFilm = () => {
    writeStorage("session", "averr-film-seen", "true"); setStage("settled");
    window.setTimeout(() => document.getElementById("work-top")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  const replay = () => {
    removeStorage("session", "averr-film-seen"); removeStorage("local", "averr-film-pathway");
    setSelected(null); setMode(null); setPaused(false); setAutoCancelled(false); setAutoCue(false); setAutoCountdown(3); setStage("question");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const showingQuestion = stage === "question" || stage === "question-exit";
  const showingCountdown = stage === "countdown";
  const introPathway = isChapterIntro(stage) ? pathwayForStage(stage) : null;
  const showingWebsite = stage === "website" || stage === "website-exit";
  const showingMarketing = stage === "marketing" || stage === "marketing-exit";
  const showingAI = stage === "ai" || stage === "ai-exit";
  const showingSaaS = stage === "saas" || stage === "saas-exit";
  const showingSettle = stage === "saas-exit" || stage === "settled";
  const compact = mode === "tour";

  return (
    <main id="top" className="landing-root film-landing" data-film-ready={ready ? "true" : "false"} data-film-stage={stage} data-film-mode={mode ?? "idle"} data-film-paused={paused ? "true" : "false"}>
      <div className="film-progress" data-stage={stage} aria-hidden="true"><span /></div>
      <header className="site-header film-header">
        <Link className="wordmark brand-wordmark" href="/" aria-label="Averr Studios home">
          <img className="brand-wordmark-ink" src="/brand/averr-studios-ink.svg" alt="Averr Studios" width="1800" height="420" fetchPriority="high" />
          <img className="brand-wordmark-reverse" src="/brand/averr-studios-reverse.svg" alt="" aria-hidden="true" width="1800" height="420" />
        </Link>
        <nav className="landing-nav" aria-label="Primary navigation"><a href="#work-top">Work</a><a href="#services">Services</a><a href="#founder">Founder</a><a href="#contact">Start a conversation</a></nav>
      </header>
      {stage !== "settled" && <button className="film-skip" type="button" onClick={skipFilm}>Skip to work →</button>}

      {showingQuestion && (
        <section className="film-question" data-exiting={stage === "question-exit" ? "true" : "false"} aria-labelledby="film-question-title" onPointerDown={cancelAutoStart}>
          <StudioToolOrbit />
          <div className="film-question-content">
            <p className="film-eyebrow">Averr Studios / A short studio film</p>
            <h1 id="film-question-title">What&apos;s the thing you&apos;re trying to <em>build?</em></h1>
            <p className="film-question-direction">Choose a chapter — or let the film begin.</p>
            <div className="film-pathways" aria-label="Choose the problem you are trying to solve">
              {PATHWAYS.map((pathway) => <MagneticChip key={pathway.id} {...pathway} selected={selected === pathway.id} onSelect={selectPathway} onEngage={cancelAutoStart} onHoverChange={setPathwayHover} />)}
            </div>
            <p className="film-choice-status" aria-live="polite">{autoCue ? "No selection? The countdown is about to begin." : autoCancelled ? "Choose any chapter to explore it in full" : "The guided film begins automatically"}</p>
          </div>
          <button className="film-start-tour" type="button" onClick={startTour}><span>Start the guided film</span><strong>Begin with Website <i aria-hidden="true">→</i></strong><b aria-hidden="true" /></button>
        </section>
      )}

      {showingCountdown && <FilmCountdown count={autoCountdown} />}
      {introPathway && <FilmCountdown count={autoCountdown} pathway={introPathway} />}
      <Suspense fallback={null}>
        {showingWebsite && <WebsiteAct2WebGL exiting={stage === "website-exit"} />}
        {showingMarketing && <MarketingAct2WebGL exiting={stage === "marketing-exit"} compact={compact} />}
        {showingAI && <AIAgentAct2WebGL exiting={stage === "ai-exit"} compact={compact} />}
        {showingSaaS && <SaaSSynchronizedAct2 exiting={stage === "saas-exit"} compact={compact} />}
      </Suspense>
      {mode && pathwayForStage(stage) && !isChapterIntro(stage) && <FilmDirector stage={stage} mode={mode} paused={paused} onPause={() => setPaused((value) => !value)} onNext={nextChapter} onChapter={jumpToChapter} />}
      <MatchCut stage={stage} />
      {showingSettle && <FilmSettle key="film-settle" onReplay={replay} arriving={stage === "saas-exit"} />}
    </main>
  );
}
